/**
 * Commute preferences state hook using RQ cache bridge + localStorage persistence.
 *
 * Follows the useMapPosition/useSetMapPosition pattern from useMapState.ts.
 * Persists in the RQ cache for the duration of the session and localStorage
 * across refreshes.
 */

import { useQuery, useQueryClient } from '@zg-rentals/react-query5';
import { useCallback, useEffect } from 'react';
import { type CommutePreferences, neighborhoodStateKeys } from './neighborhoodStateKeys';

const STORAGE_KEY = 'hp-commute-preferences';

const DEFAULT_PREFERENCES: CommutePreferences = {
  destination: null,
  travelMode: 'DRIVING',
};

function readFromStorage(): CommutePreferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : DEFAULT_PREFERENCES;
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function useSetCommutePreferences() {
  const queryClient = useQueryClient();

  return useCallback(
    (preferences: CommutePreferences) => {
      queryClient.setQueryData<CommutePreferences>(
        neighborhoodStateKeys.commutePreferences(),
        preferences,
      );
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
        } catch {
          // localStorage unavailable
        }
      }
    },
    [queryClient],
  );
}

export function useCommutePreferences(): CommutePreferences {
  const queryClient = useQueryClient();
  const setPreferences = useSetCommutePreferences();

  useEffect(() => {
    const existing = queryClient.getQueryData<CommutePreferences>(
      neighborhoodStateKeys.commutePreferences(),
    );
    if (!existing) {
      setPreferences(readFromStorage());
    }
  }, [queryClient, setPreferences]);

  const { data } = useQuery<CommutePreferences>({
    queryKey: neighborhoodStateKeys.commutePreferences(),
    queryFn: () => {
      return (
        queryClient.getQueryData<CommutePreferences>(
          neighborhoodStateKeys.commutePreferences(),
        ) ?? readFromStorage()
      );
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return data ?? DEFAULT_PREFERENCES;
}
